import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { ControlBar } from '../chrome/ControlBar';
import { TitleBar } from '../chrome/TitleBar';
import { AppProviders } from '../providers/AppProviders';
import { AuthGate } from '../auth/AuthGate';
import { TreeList } from './TreeList';
import { useTreeManagementPersistor } from '../persistence/PersistorContext';
import { TreeManagementInteractor, type TreeManagementViewState } from './TreeManagementInteractor';
import { useIsEditLayoutNarrowViewport } from '../ui/useIsNarrowViewport';

const MyTreesPageContent: React.FC = () => {
  const persistor = useTreeManagementPersistor();
  const isNarrowViewport = useIsEditLayoutNarrowViewport();
  
  const interactor = useMemo(() => {
    return new TreeManagementInteractor(persistor);
  }, [persistor]);

  const [viewState, setViewState] = useState<TreeManagementViewState>(() => interactor.getViewState());

  // The interactor pushes every state change (loading, optimistic updates, rollbacks),
  // so nothing here needs to re-pull view state around its async calls.
  useEffect(() => {
    interactor.setUpdateCallback(setViewState);
    interactor.fetchUserTrees();
    return () => {
      interactor.setUpdateCallback(() => {});
    };
  }, [interactor]);

  const fetchTrees = useCallback(() => {
    void interactor.fetchUserTrees();
  }, [interactor]);

  /** Rethrows the interactor's error so the modals can show it inline. */
  const throwPendingError = useCallback(() => {
    const { error } = interactor.getViewState();
    if (error) {
      throw new Error(error);
    }
  }, [interactor]);

  const handleRename = async (treeId: string, newName: string) => {
    await interactor.renameTree(treeId, newName);
    throwPendingError();
  };

  const handleDelete = async (treeId: string) => {
    await interactor.deleteTree(treeId);
    throwPendingError();
  };

  const handleToggleVisibility = async (treeId: string) => {
    await interactor.toggleTreeVisibility(treeId);
    throwPendingError();
  };

  return (
    <div className="about-page-container">
      {/* On narrow viewports the control bar's compact title reads "My Trees"
          (same treatment as the tree view's tree name) and replaces the title bar. */}
      <ControlBar
        isDraft={false}
        showSaveButton={false}
        isEditMode={false}
        treeName="My Trees"
      />
      {!isNarrowViewport && <TitleBar title="My Trees" />}
      <div className="about-scrollable-content">
        <TreeList
          trees={viewState.trees}
          isLoading={viewState.isLoading}
          error={viewState.error}
          onRename={handleRename}
          onDelete={handleDelete}
          onToggleVisibility={handleToggleVisibility}
          onRetry={fetchTrees}
        />
      </div>
    </div>
  );
};

export const MyTreesPage: React.FC = () => {
  return (
    <AppProviders treeId="trees">
      <AuthGate isDevelopment={false}>
        <MyTreesPageContent />
      </AuthGate>
    </AppProviders>
  );
};

